import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useHotel } from "../../contexts/HotelContext";
import {
  useBrandsQuery,
  useCreateBrandMutation,
  useUpdateBrandMutation,
  useDeleteBrandMutation,
  useTestSendEmailConfigMutation,
  useTestSendMetaCapiMutation,
} from "../../hooks/useBrands";
import { Brand, CreateBrandDto, UpdateBrandDto } from "../../types/brand";
import Layout from "../../components/Layout";

const BrandManagement: React.FC = () => {
  const { user } = useHotel();
  const navigate = useNavigate();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<Brand | null>(
    null
  );

  const { data: brands, isLoading, error } = useBrandsQuery();
  const createBrandMutation = useCreateBrandMutation();
  const updateBrandMutation = useUpdateBrandMutation();
  const deleteBrandMutation = useDeleteBrandMutation();

  // Redirect non-admin users
  if (user?.role !== "admin") {
    navigate("/");
    return null;
  }

  const handleCreateBrand = (brandData: CreateBrandDto) => {
    createBrandMutation.mutate(brandData, {
      onSuccess: () => {
        setIsCreateModalOpen(false);
      },
    });
  };

  const handleUpdateBrand = (brandId: string, brandData: UpdateBrandDto) => {
    updateBrandMutation.mutate(
      { brandId, data: brandData },
      {
        onSuccess: () => {
          setEditingBrand(null);
        },
      }
    );
  };

  const handleDeleteBrand = (brand: Brand) => {
    deleteBrandMutation.mutate(brand.id, {
      onSuccess: () => {
        setShowDeleteConfirm(null);
      },
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h3 className="text-red-800 font-medium">Error loading brands</h3>
          <p className="text-red-600 text-sm mt-1">
            {error instanceof Error ? error.message : "Unknown error occurred"}
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Brands</h1>
            <p className="text-neutral-600 mt-1">
              Zarządzaj markami i ich konfiguracją w systemie
            </p>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Dodaj Brand
          </button>
        </div>

        {/* Brands Table */}
        <div className="bg-white shadow-sm rounded-lg border border-neutral-200">
          <div className="px-6 py-4 border-b border-neutral-200">
            <h2 className="text-lg font-semibold text-neutral-900">
              Lista Brandów ({brands?.length || 0})
            </h2>
          </div>

          {brands && brands.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Nazwa
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Slug
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Opis
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Kolor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Domeny
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Meta CAPI
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Kit.com ESP
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Akcje
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {brands.map((brand) => (
                    <tr key={brand.id} className="hover:bg-neutral-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-neutral-900">
                          {brand.displayName}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-neutral-600 font-mono text-sm">
                          {brand.slug}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="max-w-xs">
                          {brand.description ? (
                            <div className="text-neutral-600 text-sm truncate">
                              {brand.description}
                            </div>
                          ) : (
                            <div className="text-neutral-400 text-xs italic">
                              Brak opisu
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <ColorBadge color={brand.primaryColor} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <DomainsList domains={brand.domains} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {brand.metaConversionConfig ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Pixel: {brand.metaConversionConfig.pixelId}
                          </span>
                        ) : (
                          <span className="text-neutral-400 text-xs">---</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {brand.kitComConfig?.enabled ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Włączone
                          </span>
                        ) : (
                          <span className="text-neutral-400 text-xs">---</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            brand.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {brand.isActive ? "Aktywny" : "Nieaktywny"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                        <button
                          onClick={() => setEditingBrand(brand)}
                          className="text-primary-600 hover:text-primary-900 transition-colors"
                        >
                          Edytuj
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(brand)}
                          className="text-red-600 hover:text-red-900 transition-colors"
                        >
                          Usuń
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-12 text-center">
              <div className="text-neutral-400 text-lg mb-2">Brak brandów</div>
              <p className="text-neutral-500 text-sm">
                Dodaj pierwszy brand, aby rozpocząć zarządzanie markami
              </p>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="mt-4 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Dodaj Brand
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create Brand Modal */}
      {isCreateModalOpen && (
        <CreateBrandModal
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateBrand}
          isLoading={createBrandMutation.isPending}
          error={createBrandMutation.error}
        />
      )}

      {/* Edit Brand Modal */}
      {editingBrand && (
        <EditBrandModal
          brand={editingBrand}
          onClose={() => setEditingBrand(null)}
          onSubmit={(data) => handleUpdateBrand(editingBrand.id, data)}
          isLoading={updateBrandMutation.isPending}
          error={updateBrandMutation.error}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <DeleteConfirmModal
          brand={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(null)}
          onConfirm={() => handleDeleteBrand(showDeleteConfirm)}
          isLoading={deleteBrandMutation.isPending}
          error={deleteBrandMutation.error}
        />
      )}
    </Layout>
  );
};

// Color Badge Component
const ColorBadge: React.FC<{ color: string }> = ({ color }) => {
  return (
    <div className="flex items-center space-x-2">
      <div
        className="w-6 h-6 rounded border border-neutral-300"
        style={{ backgroundColor: color }}
      ></div>
      <span className="text-xs text-neutral-600 font-mono">{color}</span>
    </div>
  );
};

// Domains List Component
const DomainsList: React.FC<{ domains: string[] }> = ({ domains }) => {
  if (domains.length === 0) {
    return <span className="text-neutral-400 text-sm">Brak domen</span>;
  }

  if (domains.length === 1) {
    return <span className="text-neutral-600 text-sm">{domains[0]}</span>;
  }

  return (
    <span className="text-neutral-600 text-sm">
      {domains.length}{" "}
      {domains.length === 1
        ? "domena"
        : domains.length < 5
          ? "domeny"
          : "domen"}
    </span>
  );
};

// Create Brand Modal Component
export const CreateBrandModal: React.FC<{
  onClose: () => void;
  onSubmit: (data: CreateBrandDto) => void;
  isLoading: boolean;
  error: Error | null;
}> = ({ onClose, onSubmit, isLoading, error }) => {
  const [formData, setFormData] = useState<CreateBrandDto>({
    slug: "",
    displayName: "",
    description: "",
    primaryColor: "#3B82F6",
    logoUrl: "",
    sidebarLogoUrl: "",
    emailConfig: {
      smtpHost: "",
      smtpPort: 587,
      smtpUser: "",
      smtpPass: "",
      fromAddress: "",
      fromName: "",
    },
    domains: [],
    isActive: true,
  });

  const [domainInput, setDomainInput] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleAddDomain = () => {
    if (domainInput && !formData.domains.includes(domainInput)) {
      setFormData({
        ...formData,
        domains: [...formData.domains, domainInput],
      });
      setDomainInput("");
    }
  };

  const handleRemoveDomain = (domain: string) => {
    setFormData({
      ...formData,
      domains: formData.domains.filter((d) => d !== domain),
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">
            Dodaj Brand
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-red-600 text-sm">
                {error instanceof Error ? error.message : "Wystąpił błąd"}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Slug *
              </label>
              <input
                type="text"
                name="slug"
                required
                value={formData.slug}
                onChange={(e) =>
                  setFormData({ ...formData, slug: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                placeholder="kiedynabory"
                pattern="^[a-z0-9-]+$"
                title="Tylko małe litery, cyfry i myślniki"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Nazwa wyświetlana *
              </label>
              <input
                type="text"
                name="displayName"
                required
                value={formData.displayName}
                onChange={(e) =>
                  setFormData({ ...formData, displayName: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="KiedyNabory.pl"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Opis
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={2}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Opcjonalny opis brandu"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Kolor główny *
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                name="primaryColor"
                value={formData.primaryColor}
                onChange={(e) =>
                  setFormData({ ...formData, primaryColor: e.target.value })
                }
                className="h-10 w-20 border border-neutral-300 rounded cursor-pointer"
              />
              <input
                type="text"
                name="primaryColorText"
                value={formData.primaryColor}
                onChange={(e) =>
                  setFormData({ ...formData, primaryColor: e.target.value })
                }
                className="flex-1 px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                pattern="^#[0-9A-Fa-f]{6}$"
                title="Format hex: #RRGGBB"
                placeholder="#3B82F6"
              />
            </div>
          </div>

          {/* Email Config Section */}
          <div className="border-t border-neutral-200 pt-4 space-y-4">
            <h4 className="text-sm font-medium text-neutral-900">
              Konfiguracja Email *
            </h4>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  SMTP Host *
                </label>
                <input
                  type="text"
                  name="smtpHost"
                  required
                  value={formData.emailConfig.smtpHost}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      emailConfig: {
                        ...formData.emailConfig,
                        smtpHost: e.target.value,
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="smtp.gmail.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  SMTP Port *
                </label>
                <input
                  type="number"
                  name="smtpPort"
                  required
                  min="1"
                  max="65535"
                  value={formData.emailConfig.smtpPort}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      emailConfig: {
                        ...formData.emailConfig,
                        smtpPort: parseInt(e.target.value),
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="587"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  SMTP User *
                </label>
                <input
                  type="text"
                  name="smtpUser"
                  required
                  value={formData.emailConfig.smtpUser}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      emailConfig: {
                        ...formData.emailConfig,
                        smtpUser: e.target.value,
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="user@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  SMTP Password *
                </label>
                <input
                  type="password"
                  name="smtpPass"
                  required
                  value={formData.emailConfig.smtpPass}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      emailConfig: {
                        ...formData.emailConfig,
                        smtpPass: e.target.value,
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  From Address *
                </label>
                <input
                  type="email"
                  name="fromEmail"
                  required
                  value={formData.emailConfig.fromAddress}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      emailConfig: {
                        ...formData.emailConfig,
                        fromAddress: e.target.value,
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="noreply@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  From Name *
                </label>
                <input
                  type="text"
                  name="fromName"
                  required
                  value={formData.emailConfig.fromName}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      emailConfig: {
                        ...formData.emailConfig,
                        fromName: e.target.value,
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="KiedyNabory.pl"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Reply-To Address
              </label>
              <input
                type="email"
                name="replyToAddress"
                value={formData.emailConfig.replyToAddress || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    emailConfig: {
                      ...formData.emailConfig,
                      replyToAddress: e.target.value,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="info@example.com"
              />
              <p className="mt-1 text-xs text-neutral-500">
                Opcjonalny adres email, na który użytkownicy mogą odpowiadać
              </p>
            </div>
          </div>

          {/* Domains Section */}
          <div className="border-t border-neutral-200 pt-4 space-y-3">
            <h4 className="text-sm font-medium text-neutral-900">Domeny</h4>

            <div className="flex space-x-2">
              <input
                type="text"
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddDomain();
                  }
                }}
                className="flex-1 px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="app.example.com"
              />
              <button
                type="button"
                onClick={handleAddDomain}
                className="px-4 py-2 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 rounded-md transition-colors"
              >
                Dodaj
              </button>
            </div>

            {formData.domains.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.domains.map((domain) => (
                  <span
                    key={domain}
                    className="inline-flex items-center px-3 py-1 bg-neutral-100 text-neutral-700 rounded-full text-sm"
                  >
                    {domain}
                    <button
                      type="button"
                      onClick={() => handleRemoveDomain(domain)}
                      className="ml-2 text-neutral-500 hover:text-neutral-700"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Active Toggle */}
          <div className="border-t border-neutral-200 pt-4">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData({ ...formData, isActive: e.target.checked })
                }
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
              />
              <span className="text-sm font-medium text-neutral-700">
                Aktywny
              </span>
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-neutral-600 hover:text-neutral-800 font-medium transition-colors"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md font-medium transition-colors"
            >
              {isLoading ? "Dodawanie..." : "Dodaj"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Maps a test-send mutation error (AxiosError-shaped) to a persistent,
// user-facing message. 400 (validation) and 429 (throttle) are distinct
// failure modes from a real SMTP error — the caller must not conflate them
// (spec 2026-07-15-mail-delivery-test-and-status, AC F1).
type TestSendErrorResult = {
  kind: "validation" | "throttle" | "network";
  message: string;
};

function mapTestSendError(error: unknown): TestSendErrorResult {
  const { status, data } =
    (error as { response?: { status?: number; data?: { message?: string | string[] } } })
      ?.response ?? {};
  const backendMessage = data?.message;

  if (status === 400) {
    const message = Array.isArray(backendMessage)
      ? backendMessage.join(", ")
      : backendMessage;
    return { kind: "validation", message: message || "Nieprawidłowy adres e-mail." };
  }
  if (status === 429) {
    const message = Array.isArray(backendMessage)
      ? backendMessage.join(", ")
      : backendMessage;
    return {
      kind: "throttle",
      message: message || "Zbyt wiele prób. Spróbuj ponownie za chwilę.",
    };
  }
  return {
    kind: "network",
    message: "Nie udało się wysłać testowego e-maila. Spróbuj ponownie.",
  };
}

type TestSendResult =
  | { kind: "success" }
  | { kind: "smtp-error"; message: string }
  | TestSendErrorResult;

// Test-send Panel (F1) — AC F1 bullets 3-4, L-5. Always tests the SAVED
// brand.emailConfig (backend-side), never the dirty form.
const EmailConfigTestSendPanel: React.FC<{
  brandId: string;
  isEmailConfigDirty: boolean;
}> = ({ brandId, isEmailConfigDirty }) => {
  const mutation = useTestSendEmailConfigMutation();
  const [targetEmail, setTargetEmail] = useState("");
  const [result, setResult] = useState<TestSendResult | null>(null);
  const [copied, setCopied] = useState(false);

  // Plain handler, not a <form onSubmit> — this panel lives inside the
  // brand-edit <form> and a nested <form> would hijack native submit/Enter
  // behavior onto the outer (save-brand) form.
  const handleSend = () => {
    setResult(null);
    setCopied(false);
    mutation.mutate(
      { brandId, targetEmail },
      {
        onSuccess: (data) => {
          setResult(
            data.ok
              ? { kind: "success" }
              : {
                  kind: "smtp-error",
                  message: data.error || "Wysyłka nie powiodła się.",
                }
          );
        },
        onError: (error) => {
          setResult(mapTestSendError(error));
        },
      }
    );
  };

  const handleCopy = async () => {
    if (result?.kind !== "smtp-error") return;
    try {
      await navigator.clipboard.writeText(result.message);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      // Clipboard API unavailable — message is still visible/selectable inline.
    }
  };

  return (
    <div className="border-t border-neutral-200 pt-4 space-y-3">
      <h4 className="text-sm font-medium text-neutral-900">Test wysyłki</h4>

      {isEmailConfigDirty && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
          <p className="text-amber-800 text-sm">
            Zapisz zmiany, aby przetestować nową konfigurację.
          </p>
        </div>
      )}

      <div className="flex gap-2 items-start">
        <div className="flex-1">
          <label htmlFor="testSendTargetEmail" className="sr-only">
            Adres testowy
          </label>
          <input
            id="testSendTargetEmail"
            type="email"
            value={targetEmail}
            onChange={(e) => setTargetEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && targetEmail && !mutation.isPending) {
                e.preventDefault();
                handleSend();
              }
            }}
            disabled={mutation.isPending}
            placeholder="test@example.com"
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <button
          type="button"
          onClick={handleSend}
          disabled={mutation.isPending || !targetEmail}
          className="px-4 py-2 bg-neutral-800 hover:bg-neutral-900 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-md font-medium transition-colors whitespace-nowrap"
        >
          {mutation.isPending ? "Wysyłanie…" : "Wyślij testowy e-mail"}
        </button>
      </div>

      {result && (
        <div aria-live="polite">
          {result.kind === "success" ? (
            <div className="bg-green-50 border border-green-200 rounded-md p-3">
              <p className="text-green-800 text-sm font-medium">
                OK — wysłano
              </p>
            </div>
          ) : (
            <div
              className={`rounded-md p-3 border ${
                result.kind === "throttle"
                  ? "bg-amber-50 border-amber-200"
                  : "bg-red-50 border-red-200"
              }`}
            >
              <p
                className={`text-sm ${
                  result.kind === "throttle"
                    ? "text-amber-800"
                    : "text-red-800"
                }`}
              >
                {result.message}
              </p>
              {result.kind === "smtp-error" && (
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="px-3 py-1 text-xs bg-white border border-red-300 text-red-700 rounded-md hover:bg-red-100"
                  >
                    Kopiuj
                  </button>
                  {copied && (
                    <span className="text-xs text-green-700">
                      Skopiowano
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Meta CAPI test-send panel — sends a synthetic CompleteRegistration with a
// required test_event_code through the SAVED brand config (backend-side, same
// path as production conversions). The event only ever appears in the Events
// Manager "Testowanie zdarzeń" tab, never in real conversion data.
const MetaCapiTestSendPanel: React.FC<{
  brandId: string;
  isMetaConfigDirty: boolean;
}> = ({ brandId, isMetaConfigDirty }) => {
  const mutation = useTestSendMetaCapiMutation();
  const [testEventCode, setTestEventCode] = useState("");
  const [result, setResult] = useState<
    | { kind: "success"; eventsReceived: number }
    | { kind: "error"; message: string }
    | null
  >(null);

  // Plain handler, not a <form onSubmit> — nested <form> would hijack the
  // outer save-brand form (same constraint as EmailConfigTestSendPanel).
  const handleSend = () => {
    setResult(null);
    mutation.mutate(
      { brandId, testEventCode: testEventCode.trim() },
      {
        onSuccess: (data) => {
          setResult(
            data.ok
              ? { kind: "success", eventsReceived: data.eventsReceived ?? 0 }
              : {
                  kind: "error",
                  message: data.error || "Wysyłka nie powiodła się.",
                }
          );
        },
        onError: (error) => {
          const { status, data } =
            (error as {
              response?: { status?: number; data?: { message?: string | string[] } };
            })?.response ?? {};
          const backendMessage = Array.isArray(data?.message)
            ? data?.message.join(", ")
            : data?.message;
          if (status === 400) {
            setResult({
              kind: "error",
              message: backendMessage || "Nieprawidłowy kod testowy.",
            });
          } else if (status === 429) {
            setResult({
              kind: "error",
              message:
                backendMessage || "Zbyt wiele prób. Spróbuj ponownie za chwilę.",
            });
          } else {
            setResult({
              kind: "error",
              message:
                "Nie udało się wysłać zdarzenia testowego. Spróbuj ponownie.",
            });
          }
        },
      }
    );
  };

  return (
    <div className="border-t border-blue-200 pt-4 space-y-3">
      <h4 className="text-sm font-medium text-neutral-900">
        Test Conversions API
      </h4>
      <p className="text-xs text-neutral-500">
        Otwórz Menedżer zdarzeń → Testowanie zdarzeń, skopiuj kod testowy
        (np. TEST45486) i wyślij zdarzenie. Test używa ZAPISANEJ konfiguracji
        i pełnej produkcyjnej ścieżki wysyłki.
      </p>

      {isMetaConfigDirty && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
          <p className="text-amber-800 text-sm">
            Zapisz zmiany, aby przetestować nową konfigurację.
          </p>
        </div>
      )}

      <div className="flex gap-2 items-start">
        <div className="flex-1">
          <label htmlFor="metaCapiTestEventCode" className="sr-only">
            Kod zdarzenia testowego
          </label>
          <input
            id="metaCapiTestEventCode"
            type="text"
            value={testEventCode}
            onChange={(e) => setTestEventCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && testEventCode.trim() && !mutation.isPending) {
                e.preventDefault();
                handleSend();
              }
            }}
            disabled={mutation.isPending}
            placeholder="TEST12345"
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
          />
        </div>
        <button
          type="button"
          onClick={handleSend}
          disabled={mutation.isPending || !testEventCode.trim()}
          className="px-4 py-2 bg-neutral-800 hover:bg-neutral-900 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-md font-medium transition-colors whitespace-nowrap"
        >
          {mutation.isPending ? "Wysyłanie…" : "Wyślij zdarzenie testowe"}
        </button>
      </div>

      {result && (
        <div aria-live="polite">
          {result.kind === "success" ? (
            <div className="bg-green-50 border border-green-200 rounded-md p-3">
              <p className="text-green-800 text-sm font-medium">
                OK — Meta przyjęło zdarzenie (events_received=
                {result.eventsReceived}). Sprawdź zakładkę „Testowanie
                zdarzeń" w Menedżerze zdarzeń.
              </p>
            </div>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-red-800 text-sm">{result.message}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Edit Brand Modal Component (similar to Create but with brand data pre-filled)
export const EditBrandModal: React.FC<{
  brand: Brand;
  onClose: () => void;
  onSubmit: (data: UpdateBrandDto) => void;
  isLoading: boolean;
  error: Error | null;
}> = ({ brand, onClose, onSubmit, isLoading, error }) => {
  const [formData, setFormData] = useState<UpdateBrandDto>({
    slug: brand.slug,
    displayName: brand.displayName,
    description: brand.description || "",
    primaryColor: brand.primaryColor,
    logoUrl: brand.logoUrl || "",
    sidebarLogoUrl: brand.sidebarLogoUrl || "",
    emailConfig: brand.emailConfig,
    domains: brand.domains,
    isActive: brand.isActive,
  });

  const [domainInput, setDomainInput] = useState("");

  // Snapshot of the SAVED emailConfig (F1 test-send always tests this value,
  // never the dirty form — the ref never updates after mount).
  const savedEmailConfigRef = useRef(brand.emailConfig);
  const isEmailConfigDirty =
    JSON.stringify(formData.emailConfig) !==
    JSON.stringify(savedEmailConfigRef.current);

  // Meta Conversions API state
  const [metaEnabled, setMetaEnabled] = useState(
    Boolean(brand.metaConversionConfig)
  );
  const [metaPixelId, setMetaPixelId] = useState(
    brand.metaConversionConfig?.pixelId || ""
  );
  const [metaAccessToken, setMetaAccessToken] = useState("");

  // Dirty when the form's Meta fields differ from the SAVED config — the
  // CAPI test-send always tests the saved value (parity with email test-send).
  const isMetaConfigDirty =
    metaAccessToken !== "" ||
    metaEnabled !== Boolean(brand.metaConversionConfig) ||
    metaPixelId !== (brand.metaConversionConfig?.pixelId || "");

  // Kit.com ESP state
  const [kitEnabled, setKitEnabled] = useState(
    Boolean(brand.kitComConfig?.enabled)
  );
  const [kitApiKey, setKitApiKey] = useState("");
  const [kitSystemTagName, setKitSystemTagName] = useState(
    brand.kitComConfig?.systemTagName || ""
  );
  const [kitAccessTagPrefix, setKitAccessTagPrefix] = useState(
    brand.kitComConfig?.accessTagPrefix || ""
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submitData: UpdateBrandDto = { ...formData };

    if (!metaEnabled) {
      submitData.metaConversionConfig = null;
    } else if (metaAccessToken) {
      submitData.metaConversionConfig = {
        pixelId: metaPixelId,
        accessToken: metaAccessToken,
      };
    } else if (metaPixelId !== (brand.metaConversionConfig?.pixelId || "")) {
      // pixelId changed but no new token - must provide token too
      submitData.metaConversionConfig = {
        pixelId: metaPixelId,
        accessToken: metaAccessToken,
      };
    }
    // If no changes to meta config → don't include it (keep existing)

    if (!kitEnabled) {
      submitData.kitComConfig = null;
    } else {
      const kitConfig: UpdateBrandDto["kitComConfig"] = {
        enabled: kitEnabled,
        systemTagName: kitSystemTagName || undefined,
        accessTagPrefix: kitAccessTagPrefix || undefined,
      };
      // Only include apiKey if a new value was entered (preserve existing when empty)
      if (kitApiKey) {
        kitConfig.apiKey = kitApiKey;
      }
      submitData.kitComConfig = kitConfig;
    }

    onSubmit(submitData);
  };

  const handleAddDomain = () => {
    if (domainInput && !formData.domains?.includes(domainInput)) {
      setFormData({
        ...formData,
        domains: [...(formData.domains || []), domainInput],
      });
      setDomainInput("");
    }
  };

  const handleRemoveDomain = (domain: string) => {
    setFormData({
      ...formData,
      domains: formData.domains?.filter((d) => d !== domain) || [],
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">
            Edytuj Brand: {brand.displayName}
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-red-600 text-sm">
                {error instanceof Error ? error.message : "Wystąpił błąd"}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Slug
              </label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={(e) =>
                  setFormData({ ...formData, slug: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                pattern="^[a-z0-9-]+$"
                title="Tylko małe litery, cyfry i myślniki"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Nazwa wyświetlana
              </label>
              <input
                type="text"
                name="displayName"
                value={formData.displayName}
                onChange={(e) =>
                  setFormData({ ...formData, displayName: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Opis
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={2}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Kolor główny
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                name="primaryColor"
                value={formData.primaryColor}
                onChange={(e) =>
                  setFormData({ ...formData, primaryColor: e.target.value })
                }
                className="h-10 w-20 border border-neutral-300 rounded cursor-pointer"
              />
              <input
                type="text"
                name="primaryColorText"
                value={formData.primaryColor}
                onChange={(e) =>
                  setFormData({ ...formData, primaryColor: e.target.value })
                }
                className="flex-1 px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                pattern="^#[0-9A-Fa-f]{6}$"
              />
            </div>
          </div>

          {/* Email Config Section */}
          <div className="border-t border-neutral-200 pt-4 space-y-4">
            <h4 className="text-sm font-medium text-neutral-900">
              Konfiguracja Email
            </h4>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  SMTP Host
                </label>
                <input
                  type="text"
                  name="smtpHost"
                  value={formData.emailConfig?.smtpHost || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      emailConfig: {
                        ...formData.emailConfig!,
                        smtpHost: e.target.value,
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  SMTP Port
                </label>
                <input
                  type="number"
                  name="smtpPort"
                  min="1"
                  max="65535"
                  value={formData.emailConfig?.smtpPort || 587}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      emailConfig: {
                        ...formData.emailConfig!,
                        smtpPort: parseInt(e.target.value),
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  SMTP User
                </label>
                <input
                  type="text"
                  name="smtpUser"
                  value={formData.emailConfig?.smtpUser || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      emailConfig: {
                        ...formData.emailConfig!,
                        smtpUser: e.target.value,
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  SMTP Password
                </label>
                <input
                  type="password"
                  name="smtpPass"
                  value={formData.emailConfig?.smtpPass || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      emailConfig: {
                        ...formData.emailConfig!,
                        smtpPass: e.target.value,
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Zostaw puste, aby nie zmieniać"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  From Address
                </label>
                <input
                  type="email"
                  name="fromEmail"
                  value={formData.emailConfig?.fromAddress || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      emailConfig: {
                        ...formData.emailConfig!,
                        fromAddress: e.target.value,
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  From Name
                </label>
                <input
                  type="text"
                  name="fromName"
                  value={formData.emailConfig?.fromName || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      emailConfig: {
                        ...formData.emailConfig!,
                        fromName: e.target.value,
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Reply-To Address
              </label>
              <input
                type="email"
                name="replyToAddress"
                value={formData.emailConfig?.replyToAddress || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    emailConfig: {
                      ...formData.emailConfig!,
                      replyToAddress: e.target.value,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="info@example.com"
              />
              <p className="mt-1 text-xs text-neutral-500">
                Opcjonalny adres email, na który użytkownicy mogą odpowiadać
              </p>
            </div>
          </div>

          {/* Test-send Panel (F1) — Edit mode only, brand.emailConfig always exists */}
          <EmailConfigTestSendPanel
            brandId={brand.id}
            isEmailConfigDirty={isEmailConfigDirty}
          />

          {/* Domains Section */}
          <div className="border-t border-neutral-200 pt-4 space-y-3">
            <h4 className="text-sm font-medium text-neutral-900">Domeny</h4>

            <div className="flex space-x-2">
              <input
                type="text"
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddDomain();
                  }
                }}
                className="flex-1 px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="app.example.com"
              />
              <button
                type="button"
                onClick={handleAddDomain}
                className="px-4 py-2 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 rounded-md transition-colors"
              >
                Dodaj
              </button>
            </div>

            {formData.domains && formData.domains.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.domains.map((domain) => (
                  <span
                    key={domain}
                    className="inline-flex items-center px-3 py-1 bg-neutral-100 text-neutral-700 rounded-full text-sm"
                  >
                    {domain}
                    <button
                      type="button"
                      onClick={() => handleRemoveDomain(domain)}
                      className="ml-2 text-neutral-500 hover:text-neutral-700"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Meta Conversions API Section */}
          <div className="border-t border-neutral-200 pt-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-neutral-900">
                Meta Conversions API
              </h4>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={metaEnabled}
                  onChange={(e) => setMetaEnabled(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                />
                <span className="text-sm text-neutral-600">Włączone</span>
              </label>
            </div>

            {metaEnabled && (
              <div className="space-y-4 bg-blue-50 border border-blue-200 rounded-md p-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Pixel ID
                  </label>
                  <input
                    type="text"
                    value={metaPixelId}
                    onChange={(e) => setMetaPixelId(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                    pattern="^\d+$"
                    title="Pixel ID może zawierać tylko cyfry"
                    placeholder="123456789012345"
                    required={metaEnabled}
                  />
                  <p className="mt-1 text-xs text-neutral-500">
                    Identyfikator Meta Pixel (tylko cyfry)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Access Token
                  </label>
                  <input
                    type="password"
                    value={metaAccessToken}
                    onChange={(e) => setMetaAccessToken(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                    placeholder={
                      brand.metaConversionConfig?.hasAccessToken
                        ? "Zostaw puste, aby nie zmieniać"
                        : "EAAxxxxxxxxxxxxxxxxxxxxxxxx"
                    }
                  />
                  {brand.metaConversionConfig?.hasAccessToken && (
                    <p className="mt-1 text-xs text-green-600">
                      Token skonfigurowany (zaszyfrowany AES-256-GCM)
                    </p>
                  )}
                  <p className="mt-1 text-xs text-neutral-500">
                    Token dostępu do Conversions API. Zostanie zaszyfrowany przed
                    zapisem.
                  </p>
                </div>

                {brand.metaConversionConfig?.hasAccessToken && (
                  <MetaCapiTestSendPanel
                    brandId={brand.id}
                    isMetaConfigDirty={isMetaConfigDirty}
                  />
                )}
              </div>
            )}
          </div>

          {/* Kit.com ESP Section */}
          <div className="border-t border-neutral-200 pt-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-neutral-900">
                Kit.com ESP
              </h4>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={kitEnabled}
                  onChange={(e) => setKitEnabled(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                />
                <span className="text-sm text-neutral-600">Włączone</span>
              </label>
            </div>

            {kitEnabled && (
              <div className="space-y-4 bg-green-50 border border-green-200 rounded-md p-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Klucz API
                  </label>
                  <input
                    type="password"
                    value={kitApiKey}
                    onChange={(e) => setKitApiKey(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                    placeholder={
                      brand.kitComConfig?.hasApiKey
                        ? "••••••••"
                        : "Wpisz klucz API Kit.com"
                    }
                  />
                  {brand.kitComConfig?.hasApiKey && (
                    <p className="mt-1 text-xs text-green-600">
                      Klucz API skonfigurowany
                    </p>
                  )}
                  <p className="mt-1 text-xs text-neutral-500">
                    Klucz API do integracji z Kit.com. Zostaw puste, aby nie
                    zmieniać.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    System Tag Name
                  </label>
                  <input
                    type="text"
                    value={kitSystemTagName}
                    onChange={(e) => setKitSystemTagName(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                    placeholder="system"
                  />
                  <p className="mt-1 text-xs text-neutral-500">
                    Opcjonalny tag systemowy dodawany do każdego subskrybenta.
                    Domyślnie: "system".
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Access Tag Prefix
                  </label>
                  <input
                    type="text"
                    value={kitAccessTagPrefix}
                    onChange={(e) => setKitAccessTagPrefix(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono"
                    placeholder="access:"
                  />
                  <p className="mt-1 text-xs text-neutral-500">
                    Opcjonalny prefiks tagów dostępu dla subskrypcji. Domyślnie:
                    "access:".
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Active Toggle */}
          <div className="border-t border-neutral-200 pt-4">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData({ ...formData, isActive: e.target.checked })
                }
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
              />
              <span className="text-sm font-medium text-neutral-700">
                Aktywny
              </span>
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-neutral-600 hover:text-neutral-800 font-medium transition-colors"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md font-medium transition-colors"
            >
              {isLoading ? "Zapisywanie..." : "Zapisz"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Delete Confirmation Modal Component
const DeleteConfirmModal: React.FC<{
  brand: Brand;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  error: Error | null;
}> = ({ brand, onClose, onConfirm, isLoading, error }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-red-900">Usuń Brand</h3>
        </div>

        <div className="p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
              <p className="text-red-600 text-sm">
                {error instanceof Error ? error.message : "Wystąpił błąd"}
              </p>
            </div>
          )}

          <p className="text-neutral-600 mb-4">
            Czy na pewno chcesz usunąć brand{" "}
            <strong>"{brand.displayName}"</strong>?
          </p>
          <p className="text-sm text-red-600 mb-4">
            Ta operacja jest nieodwracalna. Brand nie może być usunięty jeśli są
            do niego przypisane tenants.
          </p>

          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-neutral-600 hover:text-neutral-800 font-medium transition-colors"
            >
              Anuluj
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-md font-medium transition-colors"
            >
              {isLoading ? "Usuwanie..." : "Usuń"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandManagement;
